import lodash from 'lodash';
import moment from 'moment';
import * as XLSX from 'xlsx-js-style';
import {ConnectionType, DataType, TimeResolution} from '@flowmaps-com/flowmaps-typescriptmodels';
import {DateTimeField, Field, MappedField, QuantityField, validSheetName} from '../../../../common/upload/excel.utils';
import {Entity} from '../../../../handlers/entity';
import {measurementIdentity} from '../../../measurements-component/measurement-interval';

/** Excel names are case-insensitive and limited to 31 characters. */
export function connectionExportSheetNames(connections: Entity[]): Map<string, string> {
    const used = new Set(['connections']);
    const result = new Map<string, string>();
    for (const entity of connections) {
        const connection = entity.connection;
        const base = validSheetName(connection.info.code || connection.connectionId).slice(0, 31) || 'Data';
        let name = base;
        let suffix = 1;
        while (used.has(name.toLowerCase())) {
            const tail = ' (' + (++suffix) + ')';
            name = base.slice(0, 31 - tail.length) + tail;
        }
        used.add(name.toLowerCase());
        result.set(connection.connectionId, name);
    }
    return result;
}

interface ExportInterval {
    start: string;
    end: string;
    utcOffsetSeconds?: number;
    durationSeconds?: number;
}

function exportInterval(row: ExportInterval) {
    return {timeRange: {start: row.start, end: row.end},
        utcOffsetSeconds: row.utcOffsetSeconds, durationSeconds: row.durationSeconds};
}

function exportDateField(rows: ExportInterval[]): (cell: string) => Field<any> {
    const physical = rows.some(row => row.utcOffsetSeconds != null);
    // Floating Excel serials preserve local labels in the spring gap.
    return cell => physical
        ? new MappedField(new QuantityField(cell, null, 'dd-mm-yyyy hh:mm:ss'), value => value,
            value => value ? moment.utc(value).valueOf() / 86400000 + 25569 : null)
        : new DateTimeField(cell);
}

export interface ConnectionData extends ExportInterval {
    connectionId: string;
    consumption?: number;
    feedIn?: number;
    production?: number;
    [key: string]: any;
}

export function connectionCsvDataToJson(csvData: string, optionalDataTypes: DataType[] = []): ConnectionData[] {
    const rows = lodash.reject(csvData.split('\n'), lodash.isEmpty);
    const headers = rows[0].split(';');
    const dataRows = rows.slice(1);
    return dataRows.map(row => {
        const values = row.split(';');
        const timeRange = values[1].split('|');
        const result: ConnectionData = {
            connectionId: values[0],
            start: timeRange[0],
            end: timeRange[1]
        };
        const baseHeaders = new Map<string, 'consumption' | 'feedIn' | 'production'>([
            ['Consumption', 'consumption'],
            ['Feed-in', 'feedIn'],
            ['Production', 'production']
        ]);
        headers.forEach((header, index) => {
            const baseKey = baseHeaders.get(header);
            if (baseKey) {
                result[baseKey] = values[index] === '' ? null : lodash.toNumber(values[index]);
            } else if (header === 'utcOffsetSeconds' || header === 'durationSeconds') {
                if (values[index] !== '') result[header] = Number(values[index]);
            } else if (optionalDataTypes.includes(header as DataType)) {
                result[header] = header === DataType.coolingConsumption
                    ? values[index] === '' ? null : lodash.toNumber(values[index]) : values[index] || '';
            }
        });
        return result;
    });
}

export function connectionsForResolution(selectedItems: Entity[], resolution: TimeResolution): Entity[] {
    return lodash.uniqBy(selectedItems, item => item.connection.connectionId);
}

export function connectionsWithLinkedChargePoints(selectedItems: Entity[], includeChargePoints: boolean): Entity[] {
    if (!includeChargePoints) return selectedItems;

    const result = [...selectedItems];
    selectedItems.filter(item => item.connection?.info.connectionType === ConnectionType.Electricity)
        .forEach(electricity => {
            const electricityConnectionId = electricity.connection.connectionId;
            const locationConnections = electricity.location?.connections || [];
            const electricityConnectionsAtLocation = locationConnections.filter(connection =>
                connection.info.connectionType === ConnectionType.Electricity);
            locationConnections.filter(connection => connection.info.connectionType === ConnectionType.ChargePoint)
                .filter(connection => {
                    const configuredTarget = (connection.info as any).electricityConnectionId;
                    return configuredTarget === electricityConnectionId
                        || (!configuredTarget && electricityConnectionsAtLocation.length === 1);
                })
                .forEach(connection => result.push(new Entity(
                    electricity.organisation, electricity.location, connection)));
        });

    return lodash.uniqBy(result, item => item.connection?.connectionId);
}

export function mergeChargePointDataIntoElectricity(connections: Entity[], rows: ConnectionData[]): {
    connections: Entity[];
    rows: ConnectionData[];
} {
    const electricityConnections = connections.filter(entity =>
        entity.connection.info.connectionType === 'Electricity');
    if (electricityConnections.length === 0) {
        return {connections, rows};
    }

    const targetByChargePointId = new Map<string, string>();
    connections.filter(entity => entity.connection.info.connectionType === 'ChargePoint')
        .forEach(chargePoint => {
            const configuredTarget = (chargePoint.connection.info as any).electricityConnectionId;
            const locationTargets = electricityConnections.filter(electricity =>
                electricity.location?.locationId === chargePoint.location?.locationId);
            const target = electricityConnections.find(electricity =>
                electricity.connection.connectionId === configuredTarget)
                || (locationTargets.length === 1 ? locationTargets[0] : null);
            if (target) {
                targetByChargePointId.set(chargePoint.connection.connectionId, target.connection.connectionId);
            }
        });

    const mergedRows = rows.filter(row => !targetByChargePointId.has(row.connectionId))
        .map(row => ({...row}));
    const mergedByInterval = new Map(mergedRows.map(row =>
        [row.connectionId + '|' + measurementIdentity(exportInterval(row)), row]));
    rows.filter(row => targetByChargePointId.has(row.connectionId)).forEach(chargePointRow => {
        if (chargePointRow.consumption === null || chargePointRow.consumption === undefined) return;
        const targetConnectionId = targetByChargePointId.get(chargePointRow.connectionId);
        const key = targetConnectionId + '|' + measurementIdentity(exportInterval(chargePointRow));
        let targetRow = mergedByInterval.get(key);
        if (!targetRow) {
            targetRow = {
                connectionId: targetConnectionId,
                start: chargePointRow.start,
                end: chargePointRow.end,
                ...(chargePointRow.utcOffsetSeconds != null ? {utcOffsetSeconds: chargePointRow.utcOffsetSeconds} : {}),
                ...(chargePointRow.durationSeconds != null ? {durationSeconds: chargePointRow.durationSeconds} : {})
            };
            mergedRows.push(targetRow);
            mergedByInterval.set(key, targetRow);
        }
        const current = targetRow[DataType.chargePointConsumption];
        targetRow[DataType.chargePointConsumption] = (current === null || current === undefined ? 0 : current)
            + chargePointRow.consumption;
    });

    return {
        connections: connections.filter(entity => !targetByChargePointId.has(entity.connection.connectionId)),
        rows: mergedRows
    };
}


export interface ThermalDataExportRow extends ExportInterval {
    heatConsumption: number | null;
    coolingConsumption: number | null;
}

export interface ThermalDataExportSheet {
    source: Entity;
    rows: ThermalDataExportRow[];
}

/** Coalesce the two energy types only for the same EAN within the same location. */
export function buildThermalDataExport(connections: Entity[], rows: ConnectionData[]): ThermalDataExportSheet[] {
    const groups = new Map<string, {source: Entity; rows: Map<string, ThermalDataExportRow>}>();
    const sources = new Map<string, {key: string; type: string}>();
    lodash.uniqBy(connections, entity => entity.connection.connectionId)
        .filter(entity => entity.connection.info.connectionType === ConnectionType.Heat
            || entity.connection.info.connectionType === ConnectionType.Cooling)
        .forEach(entity => {
            const connection = entity.connection;
            const key = JSON.stringify([entity.organisation?.organisationId,
                entity.location?.locationId || connection.connectionId, connection.info.code || connection.connectionId]);
            if (!groups.has(key)) groups.set(key, {source: entity, rows: new Map()});
            sources.set(connection.connectionId, {key, type: connection.info.connectionType});
        });
    rows.forEach(point => {
        const source = sources.get(point.connectionId);
        if (!source) return;
        const points = groups.get(source.key).rows;
        const key = measurementIdentity(exportInterval(point));
        if (!points.has(key)) {
            points.set(key, {start: point.start, end: point.end, heatConsumption: null, coolingConsumption: null,
                ...(point.utcOffsetSeconds != null ? {utcOffsetSeconds: point.utcOffsetSeconds} : {}),
                ...(point.durationSeconds != null ? {durationSeconds: point.durationSeconds} : {})});
        }
        if (point.consumption !== null && point.consumption !== undefined) {
            const row = points.get(key);
            const field = source.type === ConnectionType.Heat ? 'heatConsumption' : 'coolingConsumption';
            row[field] = (row[field] ?? 0) + point.consumption;
        }
        if (source.type === ConnectionType.Heat && point.coolingConsumption != null) {
            const row = points.get(key);
            row.coolingConsumption = (row.coolingConsumption ?? 0) + Number(point.coolingConsumption);
        }
    });
    return [...groups.values()].map(group => ({
        source: group.source,
        rows: [...group.rows.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, row]) => row)
    }));
}

export function thermalDataExportTemplate(rows: ThermalDataExportRow[] = []): any {
    const dateField = exportDateField(rows);
    const result: any = {
        start: dateField('A$'), end: dateField('B$'),
        heatConsumption: new QuantityField('C$', null, '#,##0.########'),
        coolingConsumption: new QuantityField('D$', null, '#,##0.########')
    };
    return result;
}

export function buildConnectionDataExportTemplate(rows: ConnectionData[], connectionType?: ConnectionType | string): any {
    const result: any = {};
    let columnIndex = 0;
    const addColumn = (key: string, field: (cell: string) => Field<any>) => {
        result[key] = field(`${XLSX.utils.encode_col(columnIndex++)}$`);
    };
    const dateField = exportDateField(rows);
    addColumn('start', dateField);
    addColumn('end', dateField);
    (connectionType === ConnectionType.Heat || connectionType === ConnectionType.Cooling
        ? ['consumption'] : ['consumption', 'feedIn', 'production']).forEach(key => {
        if (rows.some(row => row[key] !== null && row[key] !== undefined)) {
            addColumn(key, cell => new QuantityField(cell));
        }
    });
    const baseKeys = new Set(['connectionId', 'start', 'end', 'consumption', 'feedIn', 'production',
        'utcOffsetSeconds', 'durationSeconds']);
    const dynamicKeys = [...new Set(rows.flatMap(row => Object.keys(row)))]
        .filter(key => !baseKeys.has(key))
        .filter(key => rows.some(row => row[key] !== '' && row[key] !== null && row[key] !== undefined));
    dynamicKeys.forEach(key => addColumn(key, cell => new QuantityField(cell)));
    return result;
}

/** Name intraday sheets for the intervals actually returned, including coarser source readings. */
export function connectionDataExportResolution(rows: ExportInterval[], requested: TimeResolution): TimeResolution | null {
    if (!rows.length || (requested !== TimeResolution.minute && requested !== TimeResolution.hour)) return requested;
    const durations = new Set(rows.map(row => row.durationSeconds ?? moment.utc(row.end).diff(moment.utc(row.start), 'seconds')));
    if (durations.size !== 1) return null;
    switch ([...durations][0]) {
        case 900: return TimeResolution.minute;
        case 3600: return TimeResolution.hour;
        case 86400: return TimeResolution.day;
        default: return null;
    }
}
